import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import FilePickButton from '../common/FilePickButton';
import { acceptFor, FILING_DOCUMENT_RULES } from './uploadRules';

/**
 * One document slot (a filed return, or a payment confirmation): a drop zone with the button inside it.
 * The Reconcile slots and the manual-filing dialog both render this one, so the two can't drift.
 *
 * - **U1:** the button is always there. It is the keyboard and touch path, and the drop zone is an addition
 *   on top of it.
 * - **U6:** a disabled slot ignores drops.
 *
 * What the files MEAN (validation, which slot each goes to) is the parent's: `routeDrop` in `uploadRules`.
 * Both paths hand the parent a list.
 */
export default function DocumentDropSlot({
  testId,
  label,
  busy,
  disabled,
  uploadedName,
  fileName,
  onFiles,
  onRemove,
  removeLabel,
  children,
}: {
  testId: string;
  label: string;
  busy?: boolean;
  disabled?: boolean;
  uploadedName?: string;
  /** See FilePickButton: set when the parent holds the chosen file (the dialog does; Reconcile uploads at once). */
  fileName?: string | null;
  /** Dropped or picked files. Return false from a pick to refuse it (its name is not shown as chosen). */
  onFiles: (files: File[]) => boolean | void;
  onRemove?: () => void;
  removeLabel?: string;
  /** The slot's outcome line, under the zone. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const inert = disabled || busy;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        role="group"
        aria-label={label}
        data-testid={testId}
        onDragOver={(e) => { e.preventDefault(); if (!inert) setOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); if (!inert) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (inert) return;
          const files = Array.from(e.dataTransfer?.files ?? []);
          if (files.length > 0) onFiles(files);
        }}
        style={{
          border: `1px dashed ${over ? 'var(--stx-dropzone-border-active)' : 'var(--colorNeutralStroke2)'}`,
          background: over ? 'var(--colorNeutralBackground1Selected, #eef6e8)' : 'transparent',
          borderRadius: 6,
          padding: 8,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 120ms, background 120ms',
        }}
      >
        <FilePickButton
          label={label}
          accept={acceptFor(FILING_DOCUMENT_RULES)}
          busy={busy}
          disabled={disabled}
          uploadedName={uploadedName}
          fileName={fileName}
          onPick={(f) => onFiles([f])}
          onRemove={onRemove}
          removeLabel={removeLabel}
        />
        {!disabled && (
          <span style={{ fontSize: 'var(--fontSizeBase200)', color: 'var(--colorNeutralForeground3)' }}>
            {t('uploadExtract.dropHint')}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
